import type { MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";

import { CrossIcon, PauseIcon, PlayIcon, UIButton } from "common/components";
import { BackgroundVideo } from "common/components/BackgroundVideo";
import { breakpoints } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { getCoachmarksAreShowing } from "domains/coachmarks/selectors";
import { VideoOverlayContent } from "domains/landing/topBanner/VideoOverlayContent";
import { getActiveModals } from "domains/modal/selectors";

const VIDEO_URL = "https://www.youtube.com/embed/16vVIzxprzw";

const StyledBackgroundVideo = styled(BackgroundVideo)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  // Edge doesn't support "cover" for video,
  // so unset the height to create a similar effect
  _:-ms-lang(x),
  & {
    height: unset;
  }
`;

const StyledForegroundVideo = styled.iframe<{ videoIsActive: boolean }>`
  position: absolute;
  top: -60px;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  opacity: 0;
  transition: opacity 0.5s ease;

  ${({ videoIsActive }) =>
    videoIsActive &&
    css`
      opacity: 1;
      z-index: 99;
    `};
`;

const StyledCrossIcon = styled(CrossIcon)<{ $videoIsActive: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  cursor: pointer;
  color: white;
  opacity: 0;
  z-index: 100;
  transition:
    color 0.3s,
    opacity 0.3s;

  &:hover {
    color: #1a2f59;
  }

  ${({ $videoIsActive }) =>
    $videoIsActive &&
    css`
      opacity: 1;
    `};
`;

const StyledCuratedBanner = styled.div<{ firstWatchVideoClick: boolean }>`
  display: flex;
  position: relative;
  box-sizing: border-box;
  color: white;
  overflow: hidden;
  padding: 4rem 1rem 2rem;

  @media ${breakpoints.medium} {
    margin-bottom: -4rem;
    padding: 8rem 3rem 2rem;
  }

  ${({ firstWatchVideoClick }) =>
    firstWatchVideoClick &&
    css`
      transition: all 0.5s ease;
    `}
`;

const StyledBannerVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  &:after {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    content: "";
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000);
  }
`;

const StyledVideoOverlayContent = styled(VideoOverlayContent)`
  flex: 1;
`;

const StyledPlayPauseButton = styled(UIButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 0.8rem;
  margin: 0 0.5rem 0.8rem 0;
  border-radius: 100%;
`;

type VideoBannerProps = { videoIsPaused: boolean };

export const VideoBanner = ({ videoIsPaused }: VideoBannerProps) => {
  const activeModals = useSelector(getActiveModals);
  const coachmarksAreShowing = useSelector(getCoachmarksAreShowing);
  const [firstWatchVideoClick, setFirstWatchVideoClick] = useState(false);
  const [videoIsActive, setVideoIsActive] = useState(false);
  const _contentRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLIFrameElement>(null);
  const track = useTrack();

  const modalIsActive = activeModals.length > 0;

  const [isBackgroundVideoPlaying, setIsBackgroundVideoPlaying] = useState(
    modalIsActive || coachmarksAreShowing || videoIsPaused,
  );

  const playPauseBackgroundVideo = () => setIsBackgroundVideoPlaying((prev) => !prev);

  const _pauseYoutubeVideo = () => {
    if (videoRef.current) {
      videoRef.current.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
    }
  };

  const _playYoutubeVideo = () => {
    if (videoRef.current) {
      videoRef.current.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', "*");
    }
  };

  const handleWatchVideoClick = (event: MouseEvent) => {
    event.preventDefault();

    track({ name: "Watch Video" });
    setVideoIsActive(true);
    setFirstWatchVideoClick(true);
  };

  const _handleCloseVideoClick = (event: MouseEvent) => {
    event.preventDefault();

    _pauseYoutubeVideo();
    setVideoIsActive(false);
  };

  useEffect(() => {
    if (modalIsActive || coachmarksAreShowing) {
      if (videoIsActive) {
        _pauseYoutubeVideo();
      }
    } else {
      if (videoIsActive) {
        _playYoutubeVideo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachmarksAreShowing, modalIsActive]);

  return (
    <StyledCuratedBanner
      firstWatchVideoClick={firstWatchVideoClick}
      style={{
        height: videoIsActive ? window.innerHeight : "unset",
      }}
    >
      <StyledBackgroundVideo shouldPauseVideo={isBackgroundVideoPlaying} />
      <StyledForegroundVideo
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
        onLoad={_playYoutubeVideo}
        ref={videoRef}
        src={videoIsActive ? `${VIDEO_URL}?enablejsapi=1` : undefined}
        title="Top Banner Video"
        videoIsActive={videoIsActive}
      />

      <StyledCrossIcon onClick={_handleCloseVideoClick} $videoIsActive={videoIsActive} />
      <StyledBannerVideoOverlay />
      <StyledVideoOverlayContent ref={_contentRef} handleWatchVideoClick={handleWatchVideoClick} />

      <StyledPlayPauseButton
        aria-label="play pause background video"
        onClick={playPauseBackgroundVideo}
        variant="neutral"
      >
        {isBackgroundVideoPlaying ? <PlayIcon width={15} height={15} /> : <PauseIcon width={15} height={15} />}
      </StyledPlayPauseButton>
    </StyledCuratedBanner>
  );
};
